import React from "react";
import _, { compact, isEmpty } from "lodash";
import { CellRenderers, Sorters } from "utils/tableUtils";
import { FINDING_STATUS_DISPLAY_TEXT, getCustomColumnTitles, RENDER_FINDING_SLA_STATUS_FILTERS, SEVERITY_NAMES } from "views/Findings/Constants";
import Vocabulary, { getVocabulary } from "components/Vocabulary";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";
import { CWE } from "views/Findings/CWE";
import { ARMORCODE_GROUP_KEY } from "utils/armorcodeGroupUtil";
import { AI_ACTIVITY_OVER_TIME_HELP_TEXT, INVENTORY_OVER_TIME_HELP_TEXT, PERFORMANCE_OVER_TIME_HELP_TEXT } from "utils/en";
import { Divider, Tooltip } from "antd";
import { CircleCheckGreenIcon, InfoIcon, LineChartIcon } from "components/Icons";
import { ageHeatMaphelperText } from "./CustomWidgets/FindingAgeHeatMap";
import { renderBooleanValues } from "views/Findings/FindingsListV3";
import { renderMultipleTicketEntriesAsRows } from "views/Findings/utils";
import FindingAssetScoreFormula from "views/Findings/FindingDetails/FindingAssetScoreFormula";
import EPSSScore from "views/Findings/EPSSScore";
import { COLORS, SEVERITY_COLOR } from "constants/Layout";
import { parseSelectOptions } from "utils/formUtils";
import User from "components/User";
import TableChartIcon from "ac-components/ac-icons/TableChartIcon";
import OverallRiskScoreChartIcon from "ac-components/ac-icons/OverallRiskScoreChartIcon";
import DonutChartIcon from "ac-components/ac-icons/DonutChartIcon";
import BarChartIcon from "ac-components/ac-icons/BarChartIcon";
import NewLineChartIcon from "ac-components/ac-icons/LineChartIcon";
import ComboChartIcon from "ac-components/ac-icons/ComboChartIcon";
import RiskMetricChartIcon from "ac-components/ac-icons/RiskMetricChartIcon";
import MetricChartIcon from "ac-components/ac-icons/MetricChartIcon";
import SlaChartIcon from "ac-components/ac-icons/SLAChartIcon";
import { getFeatureFlags_UNSAFE } from "contexts/FeatureFlagContext";
import ACSeverityTag from "ac-components/ACSeverityTag";
import ACResponsiveTags from "ac-components/ac-responsive-tags/ACResponsiveTags";
import { renderProductSubproductLink } from "hooks/useScanColumns";
import ACLink from "ac-components/ACLink/ACLink";
import ACTag from "ac-components/ac-tag/ACTag";
import ComboChartSelectionListField from "components/form/ComboChartSelectionListField";
import ACRiskRatingTag from "ac-components/ac-risk-rating-tag/ACRiskRatingTag";
import RiskScoreCellData from "components/RiskScoreCellData";
import { SourceToolPivotTableDisplayAsOptionValue, SourceToolPivotTableGroupByOptionValue } from "utils/sourceToolCustomWidgetUtil";
import { PRODUCT_SUMMARY_COLUMN_KEYS } from "utils/productCustomWidgetUtil";
import { SUBPRODUCT_SUMMARY_COLUMN_KEYS } from "utils/subproductCustomWidgetUtil";
import DevopsProcessIcon from "ac-components/ac-icons/DevopsProcessIcon";
import {
  CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE,
  WidgetTypeAIInventoryInitialValues,
} from "constants/AIEMConstants";
import { LongOrHybridText } from "ac-components/ac-long-or-hybrid-text/ACLongOrHybridText";

export const WIDGET_HEIGHT_PER_UNIT = 38;
export const WIDGET_HEIGHT_STANDARD = 288;

export const TOTAL_COLUMNS = 24;

export const CUSTOM_WIDGET_KEY = "CUSTOM_WIDGET";

export const CUSTOM_WIDGET_FINDING_DISPLAY_TYPE = {
    PIVOT_TABLE: "Pivot Table",
    SIMPLE_TABLE: "Simple Table",
    DONUT_CHART: "Donut Chart",
    BAR_CHART: "Bar Chart",
    TIME_LINE:"Time Line",
    FINDING_SUMMARY:"Finding Summary",
    FINDINGS_BY_AGE: "Findings By Age",
    OVERALL_RISK_SCORE: "Overall Risk Score",
    BURN_UP: "Burn-Up",
    VULNERABILITIES: "Vulnerabilities",
    SLA: "SLA",
    // NOTE: Vocabulary is defined in CUSTOM_WIDGET_DISPLAY_TYPE_LABEL as it can be changed
    // do not add dynamic values in this object
    PRODUCT_SUMMARY: "Product Summary",
    SUBPRODUCT_SUMMARY: "SubProduct Summary",
    PERFORMANCE_OVER_TIME: "PERFORMANCE_OVER_TIME",
    FINDING_RISK_MATRIX: "Finding Risk Matrix",
    AGE_HEATMAP: "Finding Age HeatMap",
    MTTR_BY_SEVERITY: "MTTR By Severity",
    REMEDIATION_TREND:"Remediation Trend",
    METRIC: "Metric",
    SEARCH_RESULTS: "Search Results",
    COMBO_CHART: "COMBO_CHART",
    GROUPED_FINDINGS: "GROUPED_FINDINGS",
    DUE_IN: "DUE_IN",
    HIERARCHY_SUMMARY: "HIERARCHY_SUMMARY",
    OVERALL_RISK_SCORE_FINDING: "OVERALL_RISK_SCORE_FINDING",
    RISKSCORE_DISTRIBUTION: "RISKSCORE_DISTRIBUTION",
    RISK_SCORE_TREND: "RISK_SCORE_TREND",
    FINDING_FLOW: "FINDING_FLOW",
    ADVANCED_SEARCH_RESULT: "ADVANCED_SEARCH_RESULT",
    DEVOPS_PROCESS: "DevOps Process",
    SLA_TREND: 'SLA_TREND',
    UNRESOLVED_FINDING_TIMELINE: 'UNRESOLVED_FINDING_TIMELINE'
};

const DeprecatedFindingDisplayTypes = [
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.OVERALL_RISK_SCORE_FINDING,

    // TODO - temp as we don't want allow user to create widget
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.FINDING_FLOW,
];

const WIDGET_TYPE_MAP = {
    PIVOT_TABLE: "Table",
    SIMPLE_TABLE: "Table",
    DONUT_CHART: "Donut",
    BAR_CHART: ["Bar", "Column"],
    TIME_LINE: ["Line", "Area"],
    TIME_SERIES: ["Line", "Area"],
    USAGE_TIME_SERIES: ["Line", "Area"],
    RISK_MATRIX: "Heatmap",
    BUBBLE_CHART: "Heatmap",
    FINDING_SUMMARY: "Table",
    FINDINGS_BY_AGE: "Table",
    OVERALL_RISK_SCORE: "Gauge",
    BURN_UP: ["Line", "Area"], 
    VULNERABILITIES: "Table",
    SLA: ["Sla", "Bar"],
    PRODUCT_SUMMARY: "Table",
    SUBPRODUCT_SUMMARY: "Table",
    PERFORMANCE_OVER_TIME: "Combo",
    FINDING_RISK_MATRIX: "Heatmap",
    AGE_HEATMAP: "Heatmap",
    MTTR_BY_SEVERITY: "Table",
    REMEDIATION_TREND: "Table",
    METRIC: "Metric",
    SEARCH_RESULTS: "Table",
    COMBO_CHART: "Combo",
    GROUPED_FINDINGS: "Table",
    DUE_IN: "Table",
    HIERARCHY_SUMMARY: "Table",
    OVERALL_RISK_SCORE_FINDING: "Gauge",
    RISKSCORE_DISTRIBUTION: "Bar",
    CREATED_VS_RESOLVED: "Table",
    SCAN_TREND: ["Bar", "Column"],
    EXPIRE_IN: "Table",
    SOURCE_TOOLS_COVERAGE: "Table",
    SOURCE_TOOL_SUMMARY: "Table",
    RISK_SCORE_TREND: ["Line", "Bar"],
    ASSET_COVERAGE: "Gauge",
    FINDING_FLOW: "Combo",
    ADVANCED_SEARCH_RESULT: "Table",
    DEVOPS_PROCESS: "Timeline",
    ASSET_SUMMARY: "Table",
    SLA_TREND: ["Line", "Bar"],
    UNRESOLVED_FINDING_TIMELINE: ['Line', 'Area'],
    USAGE_DONUT: "Donut",
  };
  
const CUSTOM_WIDGET_TYPE_SEARCH_TEXT = {
    PERFORMANCE_OVER_TIME: "Security Performance Over Time",
    AGE_HEATMAP: "Finding Age HeatMap",
    TIME_SERIES: "Inventory Over Time",
    USAGE_TIME_SERIES: "Activity Over Time",
}

export const CUSTOM_WIDGET_TICKET_DISPLAY_TYPE = {
    PIVOT_TABLE: "Pivot Table",
    BAR_CHART: "Bar Chart",
    DONUT_CHART: "Donut Chart",
    COMBO_CHART: "COMBO_CHART",
    METRIC: "Metric",
    HIERARCHY_SUMMARY: "HIERARCHY_SUMMARY",
    CREATED_VS_RESOLVED: "Created Vs Resolved",
}

export const CUSTOM_WIDGET_SCAN_DISPLAY_TYPE = {
    SCAN_TREND: "Scan Trend",
}

export const CUSTOM_WIDGET_EXCEPTION_DISPLAY_TYPE = {
    PIVOT_TABLE: "Pivot Table",
    EXPIRE_IN: "Expire In"
}

export const CUSTOM_WIDGET_SOURCE_TOOLS_DISPLAY_TYPE = {
    PIVOT_TABLE: "Pivot Table",
    SOURCE_TOOL_SUMMARY: "Source Tool Summary",
    SOURCE_TOOLS_COVERAGE: "Source Tools Coverage",
}

export const CUSTOM_WIDGET_ASSET_DISPLAY_TYPE = {
   ASSET_COVERAGE: "Asset Coverage",
   ASSET_SUMMARY: "ASSET_SUMMARY",
   COMBO_CHART: "COMBO_CHART",
   TIME_LINE: "TIME_LINE",
   DONUT_CHART: 'DONUT_CHART',
   PIVOT_TABLE: 'PIVOT_TABLE',
   BAR_CHART: 'BAR_CHART',
   SIMPLE_TABLE: 'SIMPLE_TABLE',
}

const DISPLAY_TYPE_FLAG = {
    FINDING_RISK_MATRIX: "RISK_SCORE_3_0",
    SLA_TREND: "SLA_TREND_V2",
    UNRESOLVED_FINDING_TIMELINE: 'UNRESOLVED_FINDINGS_TIMELINE_ENABLE',
}

const DISPLAY_TYPE_TICKET_FLAG = {};

export const TIME_LINE_WIDGET_CHART_TYPE = {
    AREA: "Area",
    LINE:"Line",
    BAR: "Bar",
    STACKED_BAR: "Stacked Bar"
}

export const TIME_LINE_WIDGET_TIME_RANGE = {
    ALL:"All",
    THIRTY:"30 Days",
    SIXTY: "60 Days",
    NINETY:"90 Days",
}

const TYPE_TO_ICON_MAP = {
    Table: <TableChartIcon className="font-size-24"/>,
    Gauge: <OverallRiskScoreChartIcon className="font-size-24"/>,
    Donut: <DonutChartIcon className="font-size-24"/>,
    Bar: <BarChartIcon className="font-size-24"/>,
    Line: <NewLineChartIcon className="font-size-24"/>,
    Combo: <ComboChartIcon className="font-size-24"/>,
    Heatmap: <RiskMetricChartIcon className="font-size-24"/>,
    Metric: <MetricChartIcon className="font-size-24"/>,
    Sla: <SlaChartIcon className="font-size-24"/>,
    Timeline: <DevopsProcessIcon className="font-size-24"/>
  };

/**
 * CUSTOM_WIDGET_DISPLAY_TYPE_OPTIONS used to display on select field
 */
export const CUSTOM_WIDGET_DISPLAY_TYPE_LABEL = {
    PRODUCT_SUMMARY: () => `${getVocabulary("product")} Summary`,
    SUBPRODUCT_SUMMARY: () => `${getVocabulary("subProduct")} Summary`,
    PERFORMANCE_OVER_TIME: () => (
            <>
                {CUSTOM_WIDGET_TYPE_SEARCH_TEXT.PERFORMANCE_OVER_TIME}{" "}
                <Tooltip title={PERFORMANCE_OVER_TIME_HELP_TEXT}>
                    <InfoIcon />
                </Tooltip>
            </>
        ),
    AGE_HEATMAP: () => (
            <>
                {CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.AGE_HEATMAP}{" "}
                <Tooltip title={ageHeatMaphelperText}>
                    <InfoIcon />
                </Tooltip>
            </>
        ),
    COMBO_CHART: "Combo Chart",
    GROUPED_FINDINGS: "Grouped Findings",
    SCAN_TREND: "Scan Trend",
    DUE_IN: "Due In",
    HIERARCHY_SUMMARY: () => `${getVocabulary("group")} Summary`,
    OVERALL_RISK_SCORE: "Risk Score Gauge",
    OVERALL_RISK_SCORE_FINDING: "Overall Risk Score (Findings)",
    MTTR_BY_SEVERITY: "Mean Time To Remediate",
    RISKSCORE_DISTRIBUTION: () => getFeatureFlags_UNSAFE().RISK_SCORE_3_0 ? `Risk Distribution` : `${getVocabulary("findingScore")} Distribution`,
    RISK_SCORE_TREND: "Risk Score Over Time",
    FINDING_FLOW: "Finding Flow",
    ADVANCED_SEARCH_RESULT: "Advanced Search Result",
    DEVOPS_PROCESS: "DevOps Process",
    FINDING_RISK_MATRIX: "Risk Matrix",
    // Asset display type labels
    ASSET_COVERAGE: "Asset Coverage",
    TIME_LINE: "Time Line",
    ASSET_SUMMARY: "Asset Summary",
    SLA_TREND: 'SLA Trend',
    DONUT_CHART: 'Donut Chart',
    PIVOT_TABLE: 'Pivot Table',
    BAR_CHART: 'Bar Chart',
    UNRESOLVED_FINDING_TIMELINE: 'Unresolved Finding Timeline',
    SIMPLE_TABLE: 'Simple Table',
};
  
export const getType = (label) => {
    const type = WIDGET_TYPE_MAP[label];
    return type ? (Array.isArray(type) ? type : [type]) : null;
};

export const CUSTOM_WIDGET_FINDING_DISPLAY_TYPE_OPTIONS = compact(_.map(
  CUSTOM_WIDGET_FINDING_DISPLAY_TYPE,
  (label, key) => {
    if(DeprecatedFindingDisplayTypes.includes(key)){
        return;
    }
    const _label = CUSTOM_WIDGET_DISPLAY_TYPE_LABEL[key] || label;
    const type = getType(key);
    return {
      label: _label,
      value: label,
      featureFlag: DISPLAY_TYPE_FLAG[key],
      searchText: CUSTOM_WIDGET_TYPE_SEARCH_TEXT[key] || _label,
      type,
      icon: type ? TYPE_TO_ICON_MAP[type[0]] : null 
    };
  }
));

export const CUSTOM_WIDGET_TICKET_DISPLAY_TYPE_OPTIONS = _.map(
    CUSTOM_WIDGET_TICKET_DISPLAY_TYPE,
    (label, key) => {
        const _label = CUSTOM_WIDGET_DISPLAY_TYPE_LABEL[key] || label;
        const type = getType(key);
        return {
            label: _label,
            value: label,
            featureFlag: DISPLAY_TYPE_TICKET_FLAG[key],
            searchText: CUSTOM_WIDGET_TYPE_SEARCH_TEXT[key] || _label,
            type,
            icon: type ? TYPE_TO_ICON_MAP[type[0]] : null 
        };
    }
);

export const CUSTOM_WIDGET_SCAN_DISPLAY_TYPE_OPTIONS = _.map(
    CUSTOM_WIDGET_SCAN_DISPLAY_TYPE,
    (label, key) => {
        const _label = CUSTOM_WIDGET_DISPLAY_TYPE_LABEL[key] || label;
        const type = getType(key);
        return {
            label: _label,
            value: label,
            searchText: CUSTOM_WIDGET_TYPE_SEARCH_TEXT[key] || _label,
            type,
            icon: type ? TYPE_TO_ICON_MAP[type[0]] : null 
        }
    //   featureFlag: DISPLAY_TYPE_FLAG[key],
    }
);
export const CUSTOM_WIDGET_EXCEPTIONS_DISPLAY_TYPE_OPTIONS = _.map(
    CUSTOM_WIDGET_EXCEPTION_DISPLAY_TYPE,
    (label, key) => {
        const _label = CUSTOM_WIDGET_DISPLAY_TYPE_LABEL[key] || label;
        const type = getType(key);
        return {
            label: _label,
            value: label,
            searchText: CUSTOM_WIDGET_TYPE_SEARCH_TEXT[key] || _label,
            type,
            icon: type ? TYPE_TO_ICON_MAP[type[0]] : null 
        }
    }
);

export const CUSTOM_WIDGET_SOURCE_TOOLS_DISPLAY_TYPE_OPTIONS = _.map(
    CUSTOM_WIDGET_SOURCE_TOOLS_DISPLAY_TYPE,
    (label, key) => {
        const _label = CUSTOM_WIDGET_DISPLAY_TYPE_LABEL[key] || label;
        const type = getType(key);
        return {
            label: _label,
            value: label,
            searchText: CUSTOM_WIDGET_TYPE_SEARCH_TEXT[key] || _label,
            type,
            icon: type ? TYPE_TO_ICON_MAP[type[0]] : null 
        }
    }
);

export const CUSTOM_WIDGET_ASSET_DISPLAY_TYPE_OPTIONS = _.map(
    CUSTOM_WIDGET_ASSET_DISPLAY_TYPE,
    (label, key) => {
        const _label = CUSTOM_WIDGET_DISPLAY_TYPE_LABEL[key] || label;
        const type = getType(key);
        return {
            label: _label,
            value: label,
            searchText: CUSTOM_WIDGET_TYPE_SEARCH_TEXT[key] || _label,
            type,
            icon: type ? TYPE_TO_ICON_MAP[type[0]] : null 
        };
    }
);

// AI Inventory specific label overrides
const AI_INVENTORY_DISPLAY_TYPE_LABEL = {
    SIMPLE_TABLE: "Inventory Table",
    DONUT_CHART: "Inventory Donut",
    USAGE_DONUT: "Activity Donut",
    TIME_SERIES: () => (
        <>
            {"Inventory Over Time"}{" "}
            <Tooltip title={INVENTORY_OVER_TIME_HELP_TEXT}>
                <InfoIcon />
            </Tooltip>
        </>
    ),
    USAGE_TIME_SERIES: () => (
        <>
            {"Activity Over Time"}{" "}
            <Tooltip title={AI_ACTIVITY_OVER_TIME_HELP_TEXT}>
                <InfoIcon />
            </Tooltip>
        </>
    ),
    RISK_MATRIX: "Inventory Heatmap",
    BUBBLE_CHART: "Inventory Bubble Plot",
};

export const CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE_OPTIONS = _.map(
    CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE,
    (label, key) => {
        const _label = AI_INVENTORY_DISPLAY_TYPE_LABEL[key] || CUSTOM_WIDGET_DISPLAY_TYPE_LABEL[key] || label;
        const type = getType(key);
        return {
            label: _label,
            value: label,
            searchText: CUSTOM_WIDGET_TYPE_SEARCH_TEXT[key] || _label,
            type,
            icon: type ? TYPE_TO_ICON_MAP[type[0]] : null
        };
    }
);

export const FINDING_TIME_LINE_WIDGET_CHART_TYPE_OPTIONS = _.map(TIME_LINE_WIDGET_CHART_TYPE, (label)=>({ label, value: label }));

export const FINDING_TIME_LINE_WIDGET_TIME_RANGE_OPTIONS = [{label: "All", value: 0}, {label: '30 days', value: 30}, {label: "60 days", value: 60}, {label: "90 days", value: 90}]

export const FINDING_COLUMNS_FOR_WIDGET = {
    severity: {
        dataIndex: "severity",
        title: <Vocabulary name="severity" />,
        key: "severity",
        render: (severity) => <ACSeverityTag value={severity}/>,
        sorter: Sorters.SEVERITY("severity", true),
        width: 100,
    },
    status: {
        dataIndex: "status",
        title: "Status",
        key: "status",
        render: status => FINDING_STATUS_DISPLAY_TEXT[status] || status,
        sorter: Sorters.STATUS("status", true),
        width: 100,
    },
    source: {
        title: "Source Tool",
        dataIndex: "source",
        key: "source",
        ellipsis: true,
        width: 150,
    },
    title: {
        title: "Summary",
        dataIndex: "title",
        key: "title",
        width: 300,
        ellipsis: true,
    },
    aatiScore: {
        title: "AATI Score",
        dataIndex: 'aatiScore',
        key: "aatiScore",
        width: 130,
        render: (text, record) => CellRenderers.VALUE_OR_NA(record.additionalDetails.aatiScore),
        featureFlag: "AATI_SCORE_COLUMN",
    },
    product: {
        title: <Vocabulary name="product" />,
        dataIndex: "product",
        key: "product",
        render: (product) => renderProductSubproductLink(product, "/product"),
        ellipsis: true,
        width: 130,
    },
    subProduct: {
        title: <Vocabulary name="subProduct" />,
        dataIndex: "subProduct",
        key: "subProduct",
        render: (subProduct) => renderProductSubproductLink(subProduct, "/subProduct"),
        ellipsis: true,
        width: 130,
    },
    environmentName: {
        title: "Environment",
        dataIndex: "environmentName",
        key: "environmentName",
        render: (text, record) => <EnvironmentName name = {record.environment} />,
        width: 120,
    },
    cwesStrings: {
        title: "CWE",
        dataIndex: "cwesStrings",
        key: "cwesStrings",
        render: (tags) => (
                <ACResponsiveTags
                  tags={tags}
                  itemRenderer={(cwe) => cwe}
                />
              ),
        width: 100,
        onCell: () => ({ className: "p-y-0" })
    },
    cve: {
        title: "CVE",
        dataIndex: "cve",
        key: "cve",
        width: 180,
        render: (cves) => (
                <ACResponsiveTags
                  tags={compact(cves)}
                  itemRenderer={(cve) => cve}
                />
              ),
        onCell: () => ({ className: "p-y-0" })
    },
    armorcodeCategory: {
        title: <Vocabulary name="category" />,
        dataIndex: "armorcodeCategory",
        key: "armorcodeCategory",
        render: CellRenderers.VALUE_OR_NA,
        width: 150,
        ellipsis: true,
    },
    unsupressTime:  {
        title: "Resurface Date",
        dataIndex: "unsupressTime",
        key: "unsupressTime",
        width: 150,
        render: CellRenderers.TIME,
    },
    scan: {
        title:"Scan ID",
        dataIndex: "scan",
        key: "scan",
        width: 90,
        render: text => `S-${text}`
    },
    baseScore: {
        title: "Base Score",
        dataIndex: "baseScore",
        key: "baseScore",
        width: 110,
        render: CellRenderers.VALUE_OR_NA,
    },
    exploitabilityScore : {
        title: "Exploitability score",
        dataIndex: "exploitabilityScore",
        key: "exploitabilityScore",
        width: 115,
        render: CellRenderers.VALUE_OR_NA
    },
    impactScore : {
        title: "Impact score",
        dataIndex: "impactScore",
        key: "impactScore",
        width: 100,
        render: CellRenderers.VALUE_OR_NA
    },
    foundOnDate : {
        title: "Finding Age",
        dataIndex: "foundOnDate",
        key: "foundOnDate",
        width: 90,
        render: CellRenderers.AGE
    },
    foundOn: {
        title: "Found On",
        dataIndex: "foundOn",
        key: "foundOn",
        width: 150,
        render: CellRenderers.TIME
    },
    slaDueDate: {
        title: <><Vocabulary name="resolution"/> Due Date</>,
        dataIndex: "slaDueDate",
        key: "slaDueDate",
        width: 120,
        render: CellRenderers.TIME_RELATIVE
    },
    remediationDueDate: {
        title: <><Vocabulary name="remediation"/> Due Date</>,
        dataIndex: "remediationDueDate",
        key: "remediationDueDate",
        width: 120,
        render: CellRenderers.TIME_RELATIVE
    },
    triageDueDate: {
        title: <><Vocabulary name="triage"/> Due Date</>,
        dataIndex: "triageDueDate",
        key: "triageDueDate",
        width: 120,
        render: CellRenderers.TIME_RELATIVE
    },
    lastConfirmDate : {
        title: <>Last <Vocabulary name={"confirmed"} /> Date</>,
        dataIndex: "lastConfirmDate",
        key: "lastConfirmDate",
        width: 150,
        render: CellRenderers.TIME
    },
    lastFalsePositiveDate : {
        title: <>Last <Vocabulary name={"falsePositive"} /> Date</>,
        dataIndex: "lastFalsePositiveDate",
        key: "lastFalsePositiveDate",
        width: 150,
        render: CellRenderers.TIME
    },
    lastOpenDate : {
        title: <>Last <Vocabulary name={"open"} /> Date</>,
        dataIndex: "lastOpenDate",
        key: "lastOpenDate",
        width: 150,
        render: CellRenderers.TIME
    },
    lastAcceptRiskDate : {
        title: <>Last <Vocabulary name={"acceptRisk"} /> Date</>,
        dataIndex: "lastAcceptRiskDate",
        key: "lastAcceptRiskDate",
        width: 150,
        render: CellRenderers.TIME
    },
    lastSuppressDate : {
        title: <>Last <Vocabulary name={"suppressed"} /> Date</>,
        dataIndex: "lastSuppressDate",
        key: "lastSuppressDate",
        width: 150,
        render: CellRenderers.TIME
    },
    productStatus : {
        title: <><Vocabulary name="product" /> Status</>,
        dataIndex: "productStatus",
        key: "productStatus",
        render: CellRenderers.VALUE_OR_NA,
        width: 100
    },
    subProductVersion : {
        title: <><Vocabulary name="subProduct" /> Version</>,
        dataIndex: "subProductVersion",
        key: "subProductVersion",
        render: CellRenderers.VALUE_OR_NA,
        width: 120
    },
    subProductStatuses: {
        title: <><Vocabulary name="subProduct" /> Status</>,
        dataIndex: "subProductStatuses",
        key: "subProductStatuses",
        width: 120,
        render: CellRenderers.VALUE_OR_NA,
    },
    owner: {
        title: <Vocabulary name="owner" />,
        dataIndex: "owner",
        key: "owner",
        render: CellRenderers.NAME_ID_MAP,
        width: 120,
        ellipsis: true,
    },
    securityOwner : {
        title:<Vocabulary name="securityOwner" />,
        dataIndex: "securityOwner",
        key: "securityOwner",
        render: CellRenderers.NAME_ID_MAP,
        width: 120,
        ellipsis: true,
    },
    armorcodeProjects: {
        title: <><Vocabulary name="project" isPlural={true}/></>,
        dataIndex: "armorcodeProjects",
        key: "armorcodeProjects",
        width: 130,
        render: (p, record) => {
                return (
                  <ACResponsiveTags
                    tags={record.armorcodeProjects?.map((p) => p.name)}
                  />
                );
              },
        ellipsis: true
    },
    armorcodeTicket: {
        title: "Ticket ID",
        dataIndex: "armorcodeTicket",
        key: "armorcodeTicket",
        render: (armorcodeTicket, record) => {
            if (isEmpty(record.armorcodeTicket)) {
              return CellRenderers.VALUE_OR_NA();
            }
            return record.armorcodeTicket.map((entry, index) => {
              const {
                isArmorcodeTicketClosed,
                armorcodeTicketUrl,
                armorcodeTicketId,
              } = entry;
    
              return (
                <div key={index}>
                  <ACTag
                    label={armorcodeTicketId}
                  >
                    {isArmorcodeTicketClosed ? (
                      <CircleCheckGreenIcon className="m-r-xs" />
                    ) : null}
                    <ACLink
                      primary={false}
                      href={armorcodeTicketUrl}
                      target="_blank"
                    >
                      {armorcodeTicketId}
                    </ACLink>
                  </ACTag>
                  {index !== record.armorcodeTicket.length - 1 ? (
                    <Divider className="m-y-xs" />
                  ) : null}
                </div>
              );
            });
          },
        width: 130,
    },
    armorcodeTicketStatus: {
        title: "Ticket Status",
        dataIndex: "armorcodeTicketStatus",
        key: "armorcodeTicketStatus",
        render: (armorcodeTicket, record) => renderMultipleTicketEntriesAsRows(record.armorcodeTicket, "armorcodeTicketStatus"),
        width: 130,
    },
    vendor: {
        title: "Vendor",
        dataIndex: "vendor",
        key: "vendor",
        render: CellRenderers.VALUE_OR_NA,
        width: 110,
    },
    toolId: {
        title: "Tool Finding ID",
        dataIndex: "toolId",
        key: "toolId",
        render: (value,record) => {
        value = CellRenderers.VALUE_OR_NA(value);
        if (value === CellRenderers.VALUE_OR_NA()) {
          return value;
        }
        return record.url ? (
            <ACLink href={record.url} target="_blank" title={value}>
              {value}
            </ACLink>
          ) : (
            value
          )
        },
        width: 150, 
        ellipsis: true
    },
    toolSeverity : {
        title: <>Tool <Vocabulary name="severity" /></>,
        dataIndex: "toolSeverity",
        key: "toolSeverity",
        render: (severity) => <ACSeverityTag value={severity} isCustomizedValue={false} />,
        width: 100,
    },
    toolFindingStatus: {
        title: "Tool Finding Status",
        dataIndex: "toolFindingStatus",
        key: "toolFindingStatus",
        render: (_, record) => CellRenderers.VALUE_OR_NA(record.additionalDetails?.toolStatusOriginal),
        width: 120,
    },
    findingCategory: {
        title: <>Tool Finding <Vocabulary name="category" /></>,
        dataIndex: "findingCategory",
        key: "findingCategory",
        render: CellRenderers.VALUE_OR_NA,
        width: 140,
    },
    componentName: {
        title: "Component Name",
        dataIndex: "componentName",
        key: "componentName",
        render: CellRenderers.VALUE_OR_NA,
        width: 120,
        ellipsis: true
    },
    componentVersion: {
        title: "Component Affected Version",
        dataIndex: "componentVersion",
        key: "componentVersion",
        render: CellRenderers.VALUE_OR_NA,
        width: 170,
        ellipsis: true
    },
    componentFixVersions: {
        title: "Component Fix Version",
        dataIndex: "componentFixVersions",
        key: "componentFixVersions",
        render: (text, record) => {
                const fixVersion = record.additionalDetails?.componentFixVersions;
                if (fixVersion) {
                  return <ACResponsiveTags tags={fixVersion?.split(",")} />;
                }
                return CellRenderers.VALUE_OR_NA();
              },
        width: 140,
    },
    fixability: {
        title: "Fixable Using Tool",
        dataIndex: "fixability",
        key: "fixability",
        render: (value, record) => renderBooleanValues(record.additionalDetails.fixability),
        width: 150
    }, 
    SNYK_exploitMaturity: {
        title: "Exploit Maturity",
        dataIndex: "SNYK_exploitMaturity",
        key: "SNYK_exploitMaturity",
        render: CellRenderers.VALUE_OR_NA,
        width: 150
    }, 
    repositoryName: {
        title: "Repository",
        dataIndex: "repositoryName",
        key: "repositoryName",
        render: (__, record) => {
            const repositoryName = record.additionalDetails?.repositoryName;
            return repositoryName ? (
              record.additionalDetails?.repositoryUrl ? (
                <ACLink
                  href={record.additionalDetails.repositoryUrl.toString()}
                  target="_blank"
                >
                  {repositoryName}
                </ACLink>
              ) : (
                repositoryName
              )
            ) : (
              CellRenderers.VALUE_OR_NA(repositoryName)
            );
          },
        width: 150
    }, 
    filePath: {
        title: "File Name",
        dataIndex: "filePath",
        key: "filePath",
        render: CellRenderers.VALUE_OR_NA,
        width: 250,
        ellipsis: true
    }, 
    developer: {
        title: "Developer",
        dataIndex: "developer",
        key: "developer",
        render: CellRenderers.VALUE_OR_NA,
        width: 110
    },
    newUrl: {
        title: "URL/Endpoint",
        dataIndex: "newUrl",
        key: "newUrl",
        render: CellRenderers.VALUE_OR_NA,
        width: 250,
        ellipsis: true
    }, 
    containerImageName: {
        title: "Image Name",
        dataIndex: "containerImageName",
        key: "containerImageName",
        render: (text, record) => {
          const { containerImageName, containerImageTag } = record.additionalDetails || {};
          return containerImageName
            ? containerImageTag
              ? `${containerImageName}:${containerImageTag}`
              : containerImageName
            : CellRenderers.VALUE_OR_NA();
        },
        ellipsis: true,
        width: 150
    }, 
    Twistlock_originType: {
        title: "Twistlock Origin Type",
        dataIndex: "Twistlock_originType",
        key: "Twistlock_originType",
        render: (_, record) => CellRenderers.VALUE_OR_NA(record.additionalDetails?.originType),
        width: 160
    }, 
    Twistlock_scanType: {
        title: "Twistlock Source",
        dataIndex: "Twistlock_scanType",
        key: "Twistlock_scanType",
        render: (_, record) => CellRenderers.VALUE_OR_NA(record.additionalDetails?.type),
        width: 150
    }, 
    ruleId: {
        title: "AMC Rule",
        dataIndex: "ruleId",
        key: "ruleId",
        render: (_, record) => <ACResponsiveTags tags={record.ruleId} />,
        width: 130
    }, 
    policy: {
        title: "Compliance",
        dataIndex: "policy",
        key: "policy",
        width: 150,
        render: (_, record) => <ACResponsiveTags tags={record.policy} />,
        
    }, 
    lastUpdated: {
        title: "Last Changed",
        dataIndex: "lastUpdated",
        key: "lastUpdated",
        width: 120,
        render:  CellRenderers.TIME_RELATIVE
    }, 
    issueResolved: {
        title: <>Last <Vocabulary name={"mitigated"} /> Date</>,
        dataIndex: "issueResolved",
        key: "issueResolved",
        width: 150,
        render: CellRenderers.TIME
    },
    exploited: {
        title: "Exploit Available",
        dataIndex: "exploited",
        key: "exploited",
        render: (val, record) => renderBooleanValues(record.additionalDetails.exploited),
        width: 80
    },
    XRay_Path: {
        title: "XRay Path",
        dataIndex: "XRay_Path",
        key: "XRay_Path",
        render: (text, record) => CellRenderers.VALUE_OR_NA(record.additionalDetails.JFROG_XRAY_path),
        width: 150
    },
    XRay_Impacted_Artifact: {
        title: "XRay Impacted Artifact",
        dataIndex: "XRay_Impacted_Artifact",
        key: "XRay_Impacted_Artifact",
        render: (text, record) => CellRenderers.VALUE_OR_NA(record.additionalDetails.JFROG_XRAY_impacted_artifact),
        width: 150
    },
    lastTriagedDate: {
        title: <>Last <Vocabulary name={"triage"} /> Date</>,
        dataIndex: "lastTriagedDate",
        key: "lastTriagedDate",
        width: 150,
        render: CellRenderers.TIME
    },
    assetScore: {
        title: <Vocabulary name="assetScore" />,
        dataIndex: "assetScore",
        key: "assetScore",
        width: 90,
        render: (_, finding) => <FindingAssetScoreFormula finding={finding}/>,
        featureFlag: "RISK_SCORE_3_0"
    },
    vmHostIpAddresses: {
        title: "IP Addresses",
        dataIndex: "vmHostIpAddresses",
        width: 165,
        key: "vmHostIpAddresses",
        render: (text, record) => {
            const vmHostIP = record.additionalDetails.vmHostIP;
            return CellRenderers.VALUE_OR_NA(vmHostIP);
        },
        ellipsis: true
    },
    cvssVector: {
        title: "CVSS Vector",
        dataIndex: "cvssVector",
        key: "cvssVector",
        width: 165,
        render: CellRenderers.VALUE_OR_NA,
    },
    epssScore: {
        title: "EPSS Score",
        dataIndex: 'epssProbability',
        key: 'epssScore',
        width: 100,
        render: (value, record) => <EPSSScore epssScore={record.additionalDetails.epssProbability}/>, 
    },
    isCISAKEV: {
        title: "CISA KEV",
        dataIndex: 'isCISAKEV',
        key: 'isCISAKEV',
        render: (val, record) => renderBooleanValues(record.additionalDetails.isCISAKEV),
        width: 100,
    },
    cisaDueDate: {
        title: "CISA Due Date",
        dataIndex: 'cisaDueDate',
        key: 'cisaDueDate',
        width: 120,
        render: (value, record) => CellRenderers.DATE(record.additionalDetails.cisaDueDate),
    },
    ctiScore: {
        title: "CTI Score",
        dataIndex: 'ctiScore',
        key: 'ctiScore',
        width: 100,
        render: (value, record) => CellRenderers.VALUE_OR_NA(record.additionalDetails.ctiScore),
    },
    lastSeenDate: {
        title: "Last Seen Date",
        dataIndex: "lastSeenDate",
        key: "lastSeenDate",
        width: 150,
        render: CellRenderers.TIME
    },
    team: {
        title: <Vocabulary name="team" />,
        dataIndex: "team",
        key: "team",
        render: CellRenderers.NAME_ID_MAP,
        width: 150,
        ellipsis: true,
    },
    vmHostName: {
        title: "Device",
        dataIndex: "vmHostName",
        key: "vmHostName",
        render: (text, record) => {
            const vmHostName = record.additionalDetails.vmHostName;
            return CellRenderers.VALUE_OR_NA(vmHostName);
        },
        width: 150,
        ellipsis: true,
    },
    tags: {
        title: "Tags",
        dataIndex: "tags",
        key: "tags",
        render: (_, records) => {
                if (records.tags) {
                  return (
                    <ACResponsiveTags
                        tags={records.tags}
                        secondaryLabelDelimiter=":"
                        secondaryTitle="Value"
                        columnTitle="Key"
                    />
                  );
                }
                return CellRenderers.VALUE_OR_NA();
              },
        width: 160,
    },
    scanType: {
        title: "Scan Type",
        dataIndex: "scanType",
        key: "scanType",
        render: (scanTypeValue) => <ACTag label={scanTypeValue} />,
        width: 150,
        ellipsis: true
    },
    riskScore: {
        title: <Vocabulary name="riskScore" />,
        dataIndex: "riskScore",
        key: "riskScore",
        width: 130,
        render: (riskScore, record) => {
            return (
                <RiskScoreCellData
                    riskScore={riskScore}
                    record={record}
                    column={"riskScore"}
                />
            );
        },
        sorter: true,
        sortType: "number",
    }
}   

export const OTHER_FINDING_COLUMNS = {
    [ARMORCODE_GROUP_KEY]: {
        title: <Vocabulary name="group" isPlural/>,
        dataIndex: "group",
        key: "group",   
    },
    // Depricated 
    cwe: {
        title: "CWE",
        dataIndex: "cwe",
        key: "cwe",
        render: CWE,
        width: 100,
        onCell: () => ({ className: "p-y-0" })
    },
    // 
    ticketStatus: {
        title: "Ticket Status",
        dataIndex: "ticketStatus",
        key: "ticketStatus",
        render: CellRenderers.VALUE_OR_NA,
        width: 100,
        ellipsis: true,
    },
    triageSlaStatus: {
        title: <><Vocabulary name="triage"/> SLA Status</>,
        dataIndex: "triageSlaStatus",
        key: "triageSlaStatus",
        width: 120,
        render: RENDER_FINDING_SLA_STATUS_FILTERS
    },
    remediationSlaStatus: {
        title: <><Vocabulary name="remediation"/> SLA Status</>,
        dataIndex: "remediationSlaStatus",
        key: "remediationSlaStatus",
        width: 120,
        render: RENDER_FINDING_SLA_STATUS_FILTERS
    },
    resolutionSlaStatus: {
        title: <><Vocabulary name="resolution"/> SLA Status</>,
        dataIndex: "resolutionSlaStatus",
        key: "resolutionSlaStatus",
        width: 120,
        render: RENDER_FINDING_SLA_STATUS_FILTERS
    },
    assetName: {
        title: getCustomColumnTitles()["assetName"],
        dataIndex: "assetName",
        key: "assetName"
    },
    assetType: {
        title: getCustomColumnTitles()["assetType"],
        dataIndex: "assetType",
        key: "assetType"
    },
    correlationName: {
        title: "Remediation Group",
        key: "correlationName",
        dataIndex: "correlationName",
    },
    remediationAction: {
        title: "Remediation Action",
        key: "remediationAction",
        dataIndex: "remediationAction",
    },
    riskRating: {
        dataIndex: "riskRating",
        title: "Risk Rating",
        key: "riskRating",
        render: (riskRating) => <ACRiskRatingTag rating={riskRating} />,
        sorter: Sorters.RISK_RATING("riskRating", true),
        width: 100,
    },
    owaspTop10_2021: {
        title: "OWASP Top 10 - 2021",
        dataIndex: "owaspTop10_2021",
        key: "owaspTop10_2021",
        render: (value) => <LongOrHybridText value={value} />,
    },
}

export const ALL_FINDING_COLUMNS = {
    ...FINDING_COLUMNS_FOR_WIDGET,
    ...OTHER_FINDING_COLUMNS
}

/**
 * CUSTOM_WIDGET_DISPLAY_TYPE_OPTIONS used to display on select field
 */
 export const FINDING_COLUMNS_FOR_WIDGET_OPTIONS = _.map(FINDING_COLUMNS_FOR_WIDGET, (label, value) => ({ label: label.title, value }));

 export const FINDING_COLUMNS_FOR_WIDGET_PIVOT = _.pick(
    ALL_FINDING_COLUMNS,
    "severity",
    "status",
    "source",
    "slaDueDate",
    "triageDueDate",
    "remediationDueDate",
    "resolutionSlaStatus",
    "triageSlaStatus",
    "remediationSlaStatus",
    "product",
    "subProduct",
    "armorcodeCategory",
    "title",
    "tags",
    "scanType",
    "componentName",
    "componentVersion",
    "componentFixVersions",
    "isCISAKEV",
    "exploited",
    "containerImageName",
    "cve",
    "cwesStrings",
    "vmHostName",
    "assetType",
    "assetName",
    ARMORCODE_GROUP_KEY,
    "armorcodeProjects",
    "developer",
    "correlationName",
    "remediationAction",
    "repositoryName",
    "armorcodeTicket",
    "filePath",
    "riskRating",
    "owaspTop10_2021"
 );

/**
 * CUSTOM_WIDGET_DISPLAY_TYPE_OPTIONS used to display on select field
 */
 export const FINDING_COLUMNS_FOR_WIDGET_PIVOT_OPTIONS = _.map(FINDING_COLUMNS_FOR_WIDGET_PIVOT, (label, value) => ({ label: label.title, value })).concat([{label: <Vocabulary name="team" />, value: "team"}]);
 export const FINDING_COLUMNS_FOR_WIDGET_DONUT_OPTIONS = [...FINDING_COLUMNS_FOR_WIDGET_PIVOT_OPTIONS];

 export const FINDING_COLUMN_FOR_LINE_AREA = _.pick({...FINDING_COLUMNS_FOR_WIDGET,  ...OTHER_FINDING_COLUMNS}, ['product', "subProduct", "severity", "status", "scanType", "source", "riskRating"]);

 export const FINDING_COLUMN_FOR_LINE_AREA_OPTIONS = [..._.map(FINDING_COLUMN_FOR_LINE_AREA, (label, value) => ({label: label.title, value}))];

export const SUPPORTED_DASHBOARD_TEMPLATES = ["SECURITY", "CTO", "DEVELOPER", "CISO", "PRODUCT_SECURITY", "PRODUCT_SSDLC", "PROJECT"];

export const FINDING_SUMMARY_WIDGET_OPTIONS = [
    ..._.map(
        _.pick({ ...FINDING_COLUMNS_FOR_WIDGET, ...OTHER_FINDING_COLUMNS }, [
            "status",
            "severity",
            "product",
            "subProduct",
            "team",
            "device",
            "scanType",
            "source",
            "armorcodeCategory",
            "vmHostName",
            "cve",
            "cwesStrings",
            "title",
            "componentName",
            "componentVersion",
            "componentFixVersions",
            "isCISAKEV",
            "exploited",
            "containerImageName",
            "tags",
            "assetType",
            "assetName",
            "armorcodeProjects",
            "developer",
            "correlationName",
            "remediationAction",
            "repositoryName",
            "armorcodeTicket",
            "filePath",
            "riskRating",
            "owaspTop10_2021"
        ]),
        (column) => ({ label: column.title, value: column.dataIndex })
    ),
    { label: <Vocabulary name="group" />, value: ARMORCODE_GROUP_KEY },
];

export const REMEDIATION_TREND_WIDGET_OPTIONS = [
    ..._.map(
        _.pick({ ...FINDING_COLUMNS_FOR_WIDGET, ...OTHER_FINDING_COLUMNS }, [
            "product",
            "subProduct",
            "team",
        ]),
        (column) => ({ label: column.title, value: column.dataIndex })
    ),
    { label: <Vocabulary name="group" />, value: ARMORCODE_GROUP_KEY },
];

export const SLA_TYPE_OPTIONS = [
    { label: <Vocabulary name="resolution" />, value: "RESOLUTION" },
    { label: <Vocabulary name="remediation" />, value: "REMEDIATION" },
    { label: <Vocabulary name="triage" />, value: "TRIAGE" },
];

export const HEATMAP_ON_OPTIONS = [
    { label: "Found On/Finding Age", value: "foundOn" },
    { label: "CVE Published Date", value: "publishedDate" },
];

export const SEARCH_RESULTS_ROWS = [
    { label: <Vocabulary name="product" />, value: "product" },
    { label: <Vocabulary name="subProduct" />, value: "subProduct" },
    { label: <Vocabulary name="group" />, value: ARMORCODE_GROUP_KEY },
];
export const SEARCH_RESULTS_COLUMNS = [
    { label: "Finding View Filters", value: "savedSearch" },
];

export const DonutOrderBy = {
    COUNT_ASC: "countAsc",
    COUNT_DESC: "countDesc",
    NAME_ASC: "nameAsc",
    NAME_DESC: "textDesc",
}

export const TICKET_BAR_ORDER_BY_OPTIONS = [
  { label: "Ticket Count Descending", value: DonutOrderBy.COUNT_DESC },
  { label: "Ticket Count Ascending", value: DonutOrderBy.COUNT_ASC },
  { label: "A-Z", value: DonutOrderBy.NAME_ASC },
  { label: "Z-A", value: DonutOrderBy.NAME_DESC },
];

export const TICKET_COLUMNS_FOR_WIDGET = {
    title: {
        title: "Title",
        dataIndex: "title",
        width: 150,
        ellipsis: true,
    },
    scanType: {
        title: "Scan Type",
        dataIndex: "scanType",
        render: CellRenderers.VALUE_OR_NA,
        width: 150,
        ellipsis: true,
    },  
    issueType: {
        title: "Issue Type",
        dataIndex: "issueType",
        render: CellRenderers.VALUE_OR_NA,
        width: 120,
    },
    product: {
        title: <Vocabulary name="product" />,
        dataIndex: "product",
        render: CellRenderers.NAME_ID_MAP,        
        ellipsis: true,
        width: 130,
    },
    subProduct: {
        title: <Vocabulary name="subProduct" />,
        dataIndex: "subProduct",
        render: CellRenderers.NAME_ID_MAP,
        ellipsis: true,
        width: 130,
    },
    environment: {
        title: "Environment",
        dataIndex: "environment",
        render: (text) => <EnvironmentName name = {text} />,
        width: 120,
    },
    assignee: {
        title: "Assignee",
        dataIndex: "assignee",
        key: "assignee",
        width: 150,
      },
    status:  {
        title: "Ticket Status",
        dataIndex: "status",
        key: "status",
        width: 135,
        render: CellRenderers.VALUE_OR_NA,
    },
    resolutionStatus:  {
        title: "Ticket Resolution",
        dataIndex: "resolutionStatus",
        key: "resolutionStatus",
        width: 135,
        render: CellRenderers.VALUE_OR_NA,
    },
    priority: {
        title: "Priority",
        dataIndex: "priority",
        render: CellRenderers.CUSTOMIZED_SEVERITY,
        sorter: Sorters.SEVERITY("priority", true),
        width: 150,
        ellipsis: true,
    },
};

const OTHER_TICKET_COLUMNS = {
    [ARMORCODE_GROUP_KEY]: {
        title: <Vocabulary name="group" isPlural/>,
        dataIndex: "group"
    },
};

export const ALL_TICKET_COLUMNS = {
    ...TICKET_COLUMNS_FOR_WIDGET,
    ...OTHER_TICKET_COLUMNS
}
export const TICKET_COLUMNS_FOR_WIDGET_OPTIONS = _.map(TICKET_COLUMNS_FOR_WIDGET, (label, value) => ({ label: label.title, value }));

export const TICKET_COLUMNS_FOR_WIDGET_PIVOT = _.pick(ALL_TICKET_COLUMNS, "title", "status", "resolutionStatus", "priority", "product", "subProduct", "environment", "scanType", "assignee", "issueType");

export const TICKET_COLUMNS_FOR_HIERARCHY_SUMMARY = _.pick(ALL_TICKET_COLUMNS, "priority", "status");

export const TICKET_COLUMNS_FOR_HIERARCHY_SUMMARY_OPTIONS = _.map(TICKET_COLUMNS_FOR_HIERARCHY_SUMMARY, (label, value) => ({ label: label.title, value }));

export const TICKET_COLUMNS_FOR_WIDGET_PIVOT_OPTIONS = _.map(TICKET_COLUMNS_FOR_WIDGET_PIVOT, (label, value) => ({ label: label.title, value }));

export const TICKET_COLUMNS_FOR_WIDGET_PIVOT_WITH_HIERARCHY_OPTION = _.map({...TICKET_COLUMNS_FOR_WIDGET_PIVOT, ...(_.pick(ALL_TICKET_COLUMNS, ARMORCODE_GROUP_KEY))}, (label, value) => ({ label: label.title, value }))

export const TICKET_COLUMNS_FOR_WIDGET_DONUT_OPTIONS = [...TICKET_COLUMNS_FOR_WIDGET_PIVOT_OPTIONS];

// Source Tool Columns for Widget Configuration
export const SOURCE_TOOL_COLUMNS_FOR_WIDGET_OPTIONS = [
  { label: "Source Tool Name", value: "sourceToolName", disabled: true },
  { label: "Scan Count", value: "scanCount" },
  { label: "Last Scan Date", value: "lastToolScanDate" },
  { label: "Source Type", value: "sourceType" },
  { label: "Config Count", value: "loginCount" },
  { label: "Mapping Count", value: "mappingCount" },
  { label: "Tool Status", value: "toolStatus" },
];
export const ASSET_TIMELINE_COLUMNS_FOR_WIDGET_OPTIONS = [
  { label: "Status", value: "status" },
  { label: "Source Tools", value: "sourceTool" },
  { label: "Type", value: "type" },
];




export const ALL_EXCEPTION_COLUMNS = {
    product: {
        title: <Vocabulary name="product" />,
        dataIndex: "product",
        render: CellRenderers.NAME_ID_MAP,
        ellipsis: true,
        width: 130,
    },
    subProduct: {
        title: <Vocabulary name="subProduct" />,
        dataIndex: "subProduct",
        render: CellRenderers.NAME_ID_MAP,
        ellipsis: true,
        width: 130,
    },
    status: {
        dataIndex: "status",
        title: "Status",
        render: status => FINDING_STATUS_DISPLAY_TEXT[status] || status,
        sorter: Sorters.STRING("status"),
        width: 100,
    },
    createdBy: {
        title: "Submitted By",
        dataIndex: "createdBy",
        width: 120,
        render: createdBy => <User userEmail={createdBy} />
    },
    reasons: {
        title: "Reason",
        dataIndex: "reasons",
        width: 120,
        render: CellRenderers.VALUE_OR_NA
    }
};

const EXCEPTIONS_ROW_FOR_WIDGET_PIVOT = _.pick(
    ALL_EXCEPTION_COLUMNS,
    "product",
    "subProduct",
    "reasons",
    "createdBy",
 );
 const EXCEPTIONS_COLUMN_FOR_WIDGET_PIVOT = _.pick(
    ALL_EXCEPTION_COLUMNS,
    "status",
    "reasons",
 );
 const EXCEPTIONS_COLUMN_FOR_WIDGET_EXPIRE_IN = _.pick(
    ALL_EXCEPTION_COLUMNS,
    "product",
    "subProduct",
    "createdBy"
 );

 export const EXCEPTIONS_ROW_FOR_WIDGET_PIVOT_OPTIONS = _.map(EXCEPTIONS_ROW_FOR_WIDGET_PIVOT, (label, value) => ({ label: label.title, value}));
 export const EXCEPTIONS_COLUMN_FOR_WIDGET_PIVOT_OPTIONS = _.map(EXCEPTIONS_COLUMN_FOR_WIDGET_PIVOT, (label, value) => ({ label: label.title, value}));
 export const EXCEPTIONS_COLUMN_FOR_WIDGET_EXPIRE_IN_OPTIONS = _.map(EXCEPTIONS_COLUMN_FOR_WIDGET_EXPIRE_IN, (label, value) => ({ label: label.title, value}));


export const CHART_INTERVAL_OPTIONS = [
    { label: "Daily", value: "Day" },
    { label: "Weekly", value: "Week" },
    { label: "Monthly", value: "Month" },
];

export const CustomWidgetTypes = {
    FINDINGS: "Findings",
    TICKETS: "Tickets",
    SCAN: "Scan",
    TEXT_BLOCK: "Text Block",
    EXCEPTIONS: "Exceptions",
    SOURCE_TOOLS: "Source Tools",
    ASSETS: "Assets",
    AI_INVENTORY: "AI Inventory",
};

export const ComboChartXAxisOptionValue = {
    HISTORICAL: "historical",
    FOUND_ON: "foundOn",
    LAST_CONFIRMED: "lastConfirmDate",
    DUE_DATE: "slaDueDate",
    LAST_REMEDIATED: "issueResolved",
    PRODUCT: "product",
    SUBPRODUCT: "subProduct",
}

export const scanTrendCountOptionValue = {
    SCAN: "SCAN",
    GROUP: "GROUP",
    SUBGROUP: "SUBGROUP",
    SCANCONFIGURATION: "SCANCONFIGURATION"
}

export const ScanTrendXAxisOptionValue = {
    INGESTION_DATE: "createdAt",
    TOOL_SCAN_DATE: "scanDate"
}

export const COMBO_CHART_DATE_FIELDS = [
    ComboChartXAxisOptionValue.HISTORICAL,
    ComboChartXAxisOptionValue.FOUND_ON,
    ComboChartXAxisOptionValue.LAST_CONFIRMED,
    ComboChartXAxisOptionValue.DUE_DATE,
    ComboChartXAxisOptionValue.LAST_REMEDIATED,
]

export const getComboChartXAxisOptions = () => [
  {
    label: "Historical",
    value: ComboChartXAxisOptionValue.HISTORICAL,
  },
  {
    label: "Found On",
    value: ComboChartXAxisOptionValue.FOUND_ON,
  },
  {
    label: "Last Confirmed",
    value: ComboChartXAxisOptionValue.LAST_CONFIRMED,
  },
  {
    label: `${getVocabulary("resolution")} Due Date`,
    value: ComboChartXAxisOptionValue.DUE_DATE,
  },
  {
    label: `Last ${getVocabulary("mitigated")} Date`,
    value: ComboChartXAxisOptionValue.LAST_REMEDIATED,
  },
  {
    label: getVocabulary("product"),
    value: ComboChartXAxisOptionValue.PRODUCT,
  },
  {
    label: getVocabulary("subProduct"),
    value: ComboChartXAxisOptionValue.SUBPRODUCT,
  },
];

export const ComboChartTypeOptionValue = {
    STACKED_BAR: "STACKED_BAR",
    PERCENT_BAR: "PERCENT_BAR",
    STACKED_AREA: "STACKED_AREA",
    PERCENT_AREA: "PERCENT_AREA",
}
export const ComboChartIndividualChartTypeOptionValue = {
    BAR: "Bar",
    LINE: "Line",
}

export const ComboChartIndividualChartTypeOptions = [
  {
    label: "Line/Area",
    value: ComboChartIndividualChartTypeOptionValue.LINE,
  },
  {
    label: "Bar",
    value: ComboChartIndividualChartTypeOptionValue.BAR,
  },
];

export const ComboChartTypeOptions = [
  {
    label: "Stacked Bar",
    value: ComboChartTypeOptionValue.STACKED_BAR,
  },
  {
    label: "100% Stacked Bar",
    value: ComboChartTypeOptionValue.PERCENT_BAR,
  },
  {
    label: "Stacked Area",
    value: ComboChartTypeOptionValue.STACKED_AREA,
  },
  {
    label: "100% Stacked Area",
    value: ComboChartTypeOptionValue.PERCENT_AREA,
  },
];
export const NonHistoricalComboChartTypeOptions = [
  {
    label: "Stacked Bar",
    value: ComboChartTypeOptionValue.STACKED_BAR,
  },
  {
    label: "100% Stacked Bar",
    value: ComboChartTypeOptionValue.PERCENT_BAR,
  }
];
export const RiskScoreTrendChartTypeOptions = [
    {
      label: "Line",
      value: TIME_LINE_WIDGET_CHART_TYPE.LINE,
    },
    {
      label: "Bar",
      value: TIME_LINE_WIDGET_CHART_TYPE.BAR,
    },
];

export const RiskScoreTrendRiskTypeOptionValue = {
    RISKY: "RISKY",
    GROUP_MEAN: "GROUP_MEAN",
    GROUP_SUBGROUP_MEAN: "GROUP_SUBGROUP_MEAN",
}

export const getScanTrendCountOptions = () => [
    {
        label: "Scan",
        value: scanTrendCountOptionValue.SCAN
    },
    { 
        label: getVocabulary("product"), 
        value: scanTrendCountOptionValue.GROUP 
    },
    { 
        label: getVocabulary("subProduct"), 
        value: scanTrendCountOptionValue.SUBGROUP 
    },
    {
        label: "Scan Configurations",
        value: scanTrendCountOptionValue.SCANCONFIGURATION
    }
]

export const ScanTrendChartXAxisOptions = [
    {
        label: "Ingestion Date",
        value: ScanTrendXAxisOptionValue.INGESTION_DATE
    },
    {
        label: "Tool Scan Date",
        value: ScanTrendXAxisOptionValue.TOOL_SCAN_DATE
    }
]

export const DueInBucketAsOptionValue = {
    COLUMN: "column",
    ROW: "row",
}

export const DueInBucketAsOptions = [
  {
    label: "Column",
    value: DueInBucketAsOptionValue.COLUMN,
  },
  {
    label: "Row",
    value: DueInBucketAsOptionValue.ROW,
  },
];

export const TextBlockOptions = {
    MARKDOWN: "markdown",
    HTML: "html"
}

export const findingRiskMatrixOptionValue = "findings";

export const OverAllRiskScoreOptionValue = {
    FINDING: "finding",
    PRODUCT_SUBPRODUCT: "product_subproduct",
}

export const getOverAllRiskScoreTypeOptions = () => [
    {
        label: "All Findings",
        value: OverAllRiskScoreOptionValue.FINDING,
    },
    {
        label: `${getVocabulary("product")}/${getVocabulary("subProduct")}`,
        value: OverAllRiskScoreOptionValue.PRODUCT_SUBPRODUCT,
    },
];

export const AdvancedSearchResultRowOptionValue = {
    HIERARCHY: "groups",
    PRODUCT: "product",
    SUBPRODUCT: "subProduct",
}

export const getAdvancedSearchResultRowOptions = () => [
    {
        label: getVocabulary("group"),
        value: AdvancedSearchResultRowOptionValue.HIERARCHY,
    },
    {
        label: getVocabulary("product"),
        value: AdvancedSearchResultRowOptionValue.PRODUCT,
    },
    {
        label: getVocabulary("subProduct"),
        value: AdvancedSearchResultRowOptionValue.SUBPRODUCT,
    },
];

export const AdvancedSearchResultDataValueOptionValue = {
    FINDING: "finding",
    FINDING_BREAKDOWN: "finding-breakdown",
    CWE: "cwesStrings",
    CVE: "cve",
    TICKET: "armorcodeTicket",
    PRODUCT: "product",
    SUBPRODUCT: "subProduct",
    ASSET: "assetId",
    SOURCE_LIST: "source-list",
    RISK_SCORE: "riskScore",
    RISK_SCORE_MEAN: "riskScoreMean",
    RISK_RATING: "riskRating",
    RISK_RATING_MEAN: "riskRatingMean",
    LAST_SLA_FAILURE_TRIAGE: "last-sla-failure-triage",
    LAST_SLA_FAILURE_RESOLUTION: "last-sla-failure-resolution",
    LAST_SLA_FAILURE_REMEDIATION: "last-sla-failure-remediation",
    CURRENT_SLA_STATUS_TRIAGE: "current-sla-status-triage",
    CURRENT_SLA_STATUS_RESOLUTION: "current-sla-status-resolution",
    CURRENT_SLA_STATUS_REMEDIATION: "current-sla-status-remediation",
    GROUP_SUBGROUP_STATUS: "groupSubgroup.status",
    GROUP_SUBGROUP_TAGS: "groupSubgroup.tag",
    GROUP_SUBGROUP_TEAM: "groupSubgroup.team",
    GROUP_SUBGROUP_TYPE: "groupSubgroup.type",
    GROUP_SUBGROUP_TIER: "groupSubgroup.tier",
    FINDING_FORMULA: "finding-formula",
}

export const SLA_VALUE_TYPE_TO_FIELD_MAPPING = {
    [AdvancedSearchResultDataValueOptionValue.LAST_SLA_FAILURE_TRIAGE]: {
        slaStatusKey: "triageSlaStatus",
        dueDateKey: "triageDueDate",
    },
    [AdvancedSearchResultDataValueOptionValue.LAST_SLA_FAILURE_RESOLUTION]: {
        slaStatusKey: "resolutionSlaStatus",
        dueDateKey: "slaDueDate",
    },
    [AdvancedSearchResultDataValueOptionValue.LAST_SLA_FAILURE_REMEDIATION]: {
        slaStatusKey: "remediationSlaStatus",
        dueDateKey: "remediationDueDate",
    },
    [AdvancedSearchResultDataValueOptionValue.CURRENT_SLA_STATUS_TRIAGE]: {
        slaStatusKey: "triageSlaStatus",
        dueDateKey: "triageDueDate",
    },
    [AdvancedSearchResultDataValueOptionValue.CURRENT_SLA_STATUS_RESOLUTION]: {
        slaStatusKey: "resolutionSlaStatus",
        dueDateKey: "slaDueDate",
    },
    [AdvancedSearchResultDataValueOptionValue.CURRENT_SLA_STATUS_REMEDIATION]: {
        slaStatusKey: "remediationSlaStatus",
        dueDateKey: "remediationDueDate",
    },
};

export const AdvancedSearchResultDefaultLabel = {
    [AdvancedSearchResultDataValueOptionValue.GROUP_SUBGROUP_STATUS]: "Status",
    [AdvancedSearchResultDataValueOptionValue.GROUP_SUBGROUP_TAGS]: "Tags",
    [AdvancedSearchResultDataValueOptionValue.GROUP_SUBGROUP_TEAM]: "Team(s)",
    [AdvancedSearchResultDataValueOptionValue.GROUP_SUBGROUP_TIER]: "Tier",
    [AdvancedSearchResultDataValueOptionValue.GROUP_SUBGROUP_TYPE]: "Type",
    [AdvancedSearchResultDataValueOptionValue.FINDING_BREAKDOWN]: "Findings",
    [AdvancedSearchResultDataValueOptionValue.RISK_RATING]: "Risk Rating - Riskiest",
    [AdvancedSearchResultDataValueOptionValue.RISK_RATING_MEAN]: "Risk Rating - Mean",
}

export const getAdvancedSearchResultDataValueOptions = () => {
    const countOptions = [
        {
            label: "Finding",
            value: AdvancedSearchResultDataValueOptionValue.FINDING,
        },
        {
            label: "CWE",
            value: AdvancedSearchResultDataValueOptionValue.CWE,
        },
        {
            label: "CVE",
            value: AdvancedSearchResultDataValueOptionValue.CVE,
        },
        {
            label: "Ticket",
            value: AdvancedSearchResultDataValueOptionValue.TICKET,
        },
        {
            label: getVocabulary("product"),
            value: AdvancedSearchResultDataValueOptionValue.PRODUCT,
        },
        {
            label: getVocabulary("subProduct"),
            value: AdvancedSearchResultDataValueOptionValue.SUBPRODUCT,
        },
        {
            label: "Asset",
            value: AdvancedSearchResultDataValueOptionValue.ASSET,
        },
    ].map(item => ({...item, label: `${item.label} Count`})).sort(Sorters.STRING("label"));

    const slaOptions = [
        {
            label: "SLA Status Streak - Triage",
            value: AdvancedSearchResultDataValueOptionValue.LAST_SLA_FAILURE_TRIAGE,
        },
        {
            label: "SLA Status Streak - Resolution",
            value: AdvancedSearchResultDataValueOptionValue.LAST_SLA_FAILURE_RESOLUTION,
        },
        {
            label: "SLA Status Streak - Remediation",
            value: AdvancedSearchResultDataValueOptionValue.LAST_SLA_FAILURE_REMEDIATION,
        },
        {
            label: 'Current SLA Status - Triage',
            value: AdvancedSearchResultDataValueOptionValue.CURRENT_SLA_STATUS_TRIAGE
        },
        {
            label: 'Current SLA Status - Resolution',
            value: AdvancedSearchResultDataValueOptionValue.CURRENT_SLA_STATUS_RESOLUTION
        },
        {
            label: 'Current SLA Status - Remediation',
            value: AdvancedSearchResultDataValueOptionValue.CURRENT_SLA_STATUS_REMEDIATION
        }
    ];

    const listOptions = [{
        label: "Source list",
        value: AdvancedSearchResultDataValueOptionValue.SOURCE_LIST,
    }, {
        label: "Finding Breakdown",
        value: AdvancedSearchResultDataValueOptionValue.FINDING_BREAKDOWN,
    }, {
        label: "Formula - Math",
        value: AdvancedSearchResultDataValueOptionValue.FINDING_FORMULA,
    }];
    const riskScoreOption = {
        label: `${getVocabulary("riskScore")} - Riskiest`,
        value: AdvancedSearchResultDataValueOptionValue.RISK_SCORE
    }
    const riskScoreMeanOption = {
        label: `${getVocabulary("riskScore")} - Mean`,
        value: AdvancedSearchResultDataValueOptionValue.RISK_SCORE_MEAN
    }
    const riskRatingOption = {
        label: "Risk Rating - Riskiest",
        value: AdvancedSearchResultDataValueOptionValue.RISK_RATING
    }
    const riskRatingMeanOption = {
        label: "Risk Rating - Mean",
        value: AdvancedSearchResultDataValueOptionValue.RISK_RATING_MEAN
    }
    return [...countOptions, ...listOptions, riskScoreOption, riskScoreMeanOption, riskRatingOption, riskRatingMeanOption, ...slaOptions];
}

export const WidgetDisplayTypeInitialValues = {
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.DONUT_CHART]: {
        showLegends: true,
        legendPosition: 'right',
        legendDisplayValue: 'count',
        orderBy: DonutOrderBy.COUNT_DESC,
        showTotalGroups: false,
        column1: 'severity'
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.BAR_CHART]: {
        layout: "Vertical",
        orderBy: DonutOrderBy.COUNT_DESC,
        column1: "status",
        column2: "severity",
        showPercentage: false
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PERFORMANCE_OVER_TIME]: {
        showLegends: true,
        interval: "Week",
        duration: ['02 M', 'now'],
        spData: true
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.METRIC]: {
        fontSize: 64,
        abbreviatedNumber: true,
        color: "#7839ED",
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.COMBO_CHART]: {
        charts: [ComboChartSelectionListField.getDefaultValueObject()],
        aggregateBy: "Month",
        xAxisType: ComboChartXAxisOptionValue.FOUND_ON,
        showLabels: false,
        duration: ['06 M', 'now']
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.TIME_LINE]: {
        aggregateBy: "Month",
        chartType: TIME_LINE_WIDGET_CHART_TYPE.AREA,
        columnToMonitor: "severity",
        duration: ['06 M', 'now']
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.GROUPED_FINDINGS]: {
        showRiskScore: true,
        showFindingCount: true,
        sortBy: "riskScore",
        column1: "product",
        column2: "subProduct",
        maxResult: 6,
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.FINDING_SUMMARY]: {
        showRiskScore: true,
        showFindingCount: true,
        sortBy: "riskScore",
        column1: "product",
        maxResult: 6,
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SLA]: {
        bySeverity: true,
        slaType: "RESOLUTION",
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.DUE_IN]: {
        bucketAs: DueInBucketAsOptionValue.ROW,
        buckets: [7, 14, 30, 90],
        column1: "severity",
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PIVOT_TABLE]: {
        hideEmptyBucket: true,
        column1: "status",
        column2: "severity",
        maxRows: 6,
        maxCols: 6,
        showTotal: true,
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.MTTR_BY_SEVERITY]: {
        column1: "severity"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.BURN_UP]: {
        aggregateBy: "Day",
        timeRange: 90,
        showPercentage: true
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.AGE_HEATMAP]: {
        heatmapOn: "foundOn",
        buckets: [7, 14, 30, 90],
        groupBy: "severity",
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.REMEDIATION_TREND]: {
        column1: "product"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SEARCH_RESULTS]: {
        column1: "product",
        column2: "savedSearch"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SIMPLE_TABLE]: {
        columns: ['severity', 'title', 'status'],
        maxResult: 6
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.OVERALL_RISK_SCORE]: {
        dataType: OverAllRiskScoreOptionValue.FINDING,
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.FINDINGS_BY_AGE]: {
        groupBy: "severity"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.ADVANCED_SEARCH_RESULT]: {
        columns: [{ valueType: AdvancedSearchResultDataValueOptionValue.FINDING }],
        rowType: AdvancedSearchResultRowOptionValue.HIERARCHY,
        hideEmptyRows: false
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.HIERARCHY_SUMMARY]: {
        hideEmptyRows: false
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.FINDING_RISK_MATRIX]: {
        countType: "findings"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SLA_TREND]: {
        chartType: TIME_LINE_WIDGET_CHART_TYPE.AREA,
        groupBy: "severity",
        slaType: 'RESOLUTION',
        aggregateBy: 'Month',
        duration: ['06 M', 'now']
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.UNRESOLVED_FINDING_TIMELINE]: {
        chartType: TIME_LINE_WIDGET_CHART_TYPE.AREA,
        columnToMonitor: "Severity",
        timeRange: "LAST_14_DAYS"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PRODUCT_SUMMARY]: {
        activeColumnKeys: PRODUCT_SUMMARY_COLUMN_KEYS,
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SUBPRODUCT_SUMMARY]: {
        activeColumnKeys: SUBPRODUCT_SUMMARY_COLUMN_KEYS,
    },
};

// These are values for the settings which didn't existed but the feature existed
// Then later on the feature was put behind an option
// Eg: Finding summary finding counts were displayed by default, later they were put behind checkbox
// so for existing widget the setting should be default on as it doesn,t exist in the widget config 
export const WidgetDisplayTypeInitialValuesIfDoesntExist = {
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.FINDING_SUMMARY]: {
        showFindingCount: true,
        sortBy: "group",
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.BAR_CHART]: {
        orderBy: DonutOrderBy.COUNT_DESC
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SLA]: {
        bySeverity: true
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.COMBO_CHART]: {
        xAxisType: ComboChartXAxisOptionValue.HISTORICAL,
        aggregateBy: "Month",
        showLabels: true
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.TIME_LINE]: {
        aggregateBy: "Month"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PERFORMANCE_OVER_TIME]: {
        aggregateBy: "Month"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PIVOT_TABLE]: {
        hideEmptyBucket: false
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.DONUT_CHART]: {
        hideEmptyBucket: false,
        legendPosition: 'right',
        legendDisplayValue: 'count',
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.MTTR_BY_SEVERITY]: {
        column1: "severity"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.DUE_IN]: {
        column1: "severity"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.AGE_HEATMAP]: {
        buckets: [29, 59, 89, 120],
        groupBy: "severity"
    },
    [CUSTOM_WIDGET_SCAN_DISPLAY_TYPE.SCAN_TREND]: {
        color: "#2b78e4",
        countType: scanTrendCountOptionValue.SCAN
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.OVERALL_RISK_SCORE]: {
        dataType: OverAllRiskScoreOptionValue.PRODUCT_SUBPRODUCT,
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.FINDINGS_BY_AGE]: {
        groupBy: "severity"
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.HIERARCHY_SUMMARY]: {
        hideEmptyRows: false
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.ADVANCED_SEARCH_RESULT]: {
        hideEmptyRows: false
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.FINDING_RISK_MATRIX]: {
        countType: "findings"
    },
    // AI Inventory display types - defaults for legend fields
    [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.DONUT_CHART]: {
        showLegends: true,
        legendPosition: 'right',
        legendDisplayValue: 'count',
    },
    [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.USAGE_DONUT]: {
        showLegends: true,
        legendPosition: 'right',
        legendDisplayValue: 'count',
    },
    [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.TIME_SERIES]: {
        showLegends: true,
    },
    [CUSTOM_WIDGET_AI_INVENTORY_DISPLAY_TYPE.USAGE_TIME_SERIES]: {
        showLegends: true,
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PRODUCT_SUMMARY]: {
        activeColumnKeys: PRODUCT_SUMMARY_COLUMN_KEYS,
    },
    [CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SUBPRODUCT_SUMMARY]: {
        activeColumnKeys: SUBPRODUCT_SUMMARY_COLUMN_KEYS,
    },
};

export const WidgetTextBlockInitialValues = {
    contentType: TextBlockOptions.HTML
};

export const WidgetTextInitialValuesIfDoesntExist = {
    contentType: TextBlockOptions.MARKDOWN
};

export const WidgetTicketInitialValues = {
    [CUSTOM_WIDGET_TICKET_DISPLAY_TYPE.BAR_CHART]: {
        layout: "Vertical",
        orderBy: DonutOrderBy.COUNT_DESC,
    },
    [CUSTOM_WIDGET_TICKET_DISPLAY_TYPE.PIVOT_TABLE]: {
        hideEmptyBucket: true,
    },
    [CUSTOM_WIDGET_TICKET_DISPLAY_TYPE.DONUT_CHART]: {
        showLegends: true,
        legendPosition: 'right',
        legendDisplayValue: 'count',
        orderBy: DonutOrderBy.COUNT_DESC,
        groupOthers: true,
        maxValues: 25,
        showTotalGroups: true,
        hideEmptyBucket: false
    },
    [CUSTOM_WIDGET_TICKET_DISPLAY_TYPE.COMBO_CHART]: {
        charts: [ComboChartSelectionListField.getDefaultValueObject()],
        aggregateBy: "Month",
        xAxisType: ComboChartXAxisOptionValue.HISTORICAL,
        showLabels: false
    },
    [CUSTOM_WIDGET_TICKET_DISPLAY_TYPE.METRIC]: {
        fontSize: 64,
        abbreviatedNumber: false,
        showActualNumber: false
    },
    [CUSTOM_WIDGET_TICKET_DISPLAY_TYPE.CREATED_VS_RESOLVED]: {
        aggregateBy: "Day"
    },
};

const WidgetTypeScanInitialValues = {
    [CUSTOM_WIDGET_SCAN_DISPLAY_TYPE.SCAN_TREND]: {
        color: "#2b78e4",
        countType: scanTrendCountOptionValue.SCAN
    },
}

const WidgetTypeExceptionInitialValues = {
    [CUSTOM_WIDGET_EXCEPTION_DISPLAY_TYPE.EXPIRE_IN]: {
        buckets: [7, 14, 30, 60, 90]
    },
}

const WidgetTypeSourceToolInitialValues = {
    [CUSTOM_WIDGET_SOURCE_TOOLS_DISPLAY_TYPE.PIVOT_TABLE]: {
        sourceToolColumns: ["sourceToolName", "scanCount", "lastToolScanDate"],
        pivotColumn: SourceToolPivotTableGroupByOptionValue.FINDING_STATUS,
        displayValueAs: SourceToolPivotTableDisplayAsOptionValue.NUMBER,
        severity: [SEVERITY_NAMES.critical, SEVERITY_NAMES.high],
    },
    [CUSTOM_WIDGET_SOURCE_TOOLS_DISPLAY_TYPE.SOURCE_TOOL_SUMMARY]: {
        sourceToolColumns: ["sourceToolName", "lastToolScanDate", "sourceType", "loginCount", "mappingCount", "toolStatus"],
    },
}

const WidgetTypeAssetInitialValues = {
    [CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.TIME_LINE]: {
        aggregateBy: "Month",
        chartType: TIME_LINE_WIDGET_CHART_TYPE.AREA,
        // columnToMonitor: "severity",
        duration: ['06 M', 'now']
    },
    [CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.COMBO_CHART]: {
        charts: [ComboChartSelectionListField.getDefaultValueObject()],
        aggregateBy: "Month",
        xAxisType: ComboChartXAxisOptionValue.HISTORICAL,
        showLabels: false,
        duration: ['06 M', 'now']
    },
    [CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.DONUT_CHART]: {
        showLegends: true,
        legendPosition: 'right',
        legendDisplayValue: 'count',
        orderBy: DonutOrderBy.COUNT_DESC,
        showTotalGroups: false,
    },
    [CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.PIVOT_TABLE]: {
        hideEmptyBucket: true,
        column1: "armorCodeType",
        column2: "active",
        maxRows: 6,
        maxCols: 6,
        showTotal: true,
    },
    [CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.BAR_CHART]: {
        layout: "Vertical",
        orderBy: DonutOrderBy.COUNT_DESC,
        column1: "armorCodeType",
        column2: "active",
        showPercentage: false
    },
    [CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.SIMPLE_TABLE]: {
        columns: ['id', 'name', 'riskScore', 'findingsBreakdown', 'active'],
        maxResult: 10
    },
}


export const CustomWidgetInitialValues  = {
    [CustomWidgetTypes.FINDINGS]: WidgetDisplayTypeInitialValues,
    [CustomWidgetTypes.TICKETS]: WidgetTicketInitialValues,
    [CustomWidgetTypes.SCAN]: WidgetTypeScanInitialValues,
    [CustomWidgetTypes.EXCEPTIONS]: WidgetTypeExceptionInitialValues,
    [CustomWidgetTypes.SOURCE_TOOLS]: WidgetTypeSourceToolInitialValues,
    [CustomWidgetTypes.ASSETS]: WidgetTypeAssetInitialValues,
    [CustomWidgetTypes.AI_INVENTORY]: WidgetTypeAIInventoryInitialValues,
}

export const CustomWidgetFilterType = {
  BASIC: "basic",
  VISUAL: "visual",
  AQL: "aql",
  SAVED_SEARCH: "savedSearch",
};

export const FindingSortingOptions = [
  {
    label: "Risk Score",
    value: "riskScore",
  },
  {
    label: "Finding Count",
    value: "findingCount",
  },
];
export const FindingSortingOptionsWithGroup = [
  {
    label: "Risk Score",
    value: "riskScore",
  },
  {
    label: "Finding Count",
    value: "findingCount",
  },
  {
    label: "Group",
    value: "group",
  },
];

export const FINDING_SLA_STATUS_COLORS = {
    pass: COLORS.success,
    failed: SEVERITY_COLOR.critical,
    "in progress": COLORS.inprogress,
    "N/A": COLORS.default,
  }

export const parseSavedSearchOptions = (options, filterOptions = {}) => {
    const { onlyTimeSeries = false, disableAQLv2 = false } = filterOptions;
    const parsedOptions = parseSelectOptions(options);
    
    // ENG-90131: Filter options based on storeTimeSeries if needed
    const filteredOptions = onlyTimeSeries 
      ? parsedOptions.filter(savedSearch => savedSearch.storeTimeSeries)
      : parsedOptions;
    
    return filteredOptions.map((savedSearch) => {
        const hasAQLv2 = savedSearch.filters.includes("aqlQueryV2");
        let label = savedSearch.storeTimeSeries ? (
            <>
                <LineChartIcon /> {savedSearch.label}
                </>
            ) : (
                <span className="m-l-m">{savedSearch.label}</span>
            );
        if (hasAQLv2 && disableAQLv2) {
          label = (
            <Tooltip
                placement="right"
                title={
                    <>
                        <div className="text-bold">AQLv2 View</div>This widget does
                        not support AQLv2 based filter yet.
                    </>
                }
            >
                <div className="d-flex justify-content-between">
                    <span>{label}</span>
                    <span>AQLv2</span>
                </div>
            </Tooltip>
          );
        }
        return {
            ...savedSearch,
            label,
            disabled: disableAQLv2 ? hasAQLv2 : false
        }
    });
};


export const TicketComboChartXAxisOptionValue = {
    HISTORICAL: "historical",
    PRODUCT: "product",
    SUBPRODUCT: "subProduct",
    TEAM: "team"
}

export const AssetComboChartXAxisOptionValue = {
    HISTORICAL: "historical"
}

export const getTicketComboChartOptions = () => [
  {
    label: "Historical",
    value: TicketComboChartXAxisOptionValue.HISTORICAL,
  },
  {
    label: getVocabulary("product"),
    value: TicketComboChartXAxisOptionValue.PRODUCT,
  },
  {
    label: getVocabulary("subProduct"),
    value: TicketComboChartXAxisOptionValue.SUBPRODUCT,
  },
  {
    label: getVocabulary("team"),
    value: TicketComboChartXAxisOptionValue.TEAM,
  },
];
export const getAssetComboChartOptions = () => [
  {
    label: "Historical",
    value: TicketComboChartXAxisOptionValue.HISTORICAL,
  }
];

export const AQLV2_NOT_SUPPORTED_BUT_SAVEDSEARCH_SUPPORTED_WIDGETS = [
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PRODUCT_SUMMARY,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SUBPRODUCT_SUMMARY,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.HIERARCHY_SUMMARY,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.BURN_UP,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.COMBO_CHART,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.REMEDIATION_TREND,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PERFORMANCE_OVER_TIME,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.TIME_LINE,
];

export const AQL_NOT_SUPPORTD_WIDGETS = [
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.BURN_UP,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.COMBO_CHART,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.HIERARCHY_SUMMARY,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PRODUCT_SUMMARY,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.REMEDIATION_TREND,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SEARCH_RESULTS,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.PERFORMANCE_OVER_TIME,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SUBPRODUCT_SUMMARY,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.TIME_LINE,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.RISK_SCORE_TREND,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.ADVANCED_SEARCH_RESULT,
    CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.ASSET_COVERAGE,
    CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.TIME_LINE,
    CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.COMBO_CHART,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SLA_TREND,
    CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.DONUT_CHART,
    CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.PIVOT_TABLE,
    CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.BAR_CHART,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.UNRESOLVED_FINDING_TIMELINE,
    CUSTOM_WIDGET_ASSET_DISPLAY_TYPE.SIMPLE_TABLE
];

export const SAVED_SEARCH_NOT_SUPPORTD_WIDGETS = [
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.RISK_SCORE_TREND,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.SLA_TREND,
    CUSTOM_WIDGET_FINDING_DISPLAY_TYPE.UNRESOLVED_FINDING_TIMELINE
];

export const MTTROptionValue = {
    SEVERITY: "severity",
    PRODUCT: "product",
    SUBPRODUCT: "subProduct",
    RISK_RATING: "riskRating"
}

export const getMTTROptions = () => [
    {
        label: "Severity",
        value: MTTROptionValue.SEVERITY,
    },
    {
        label: getVocabulary("product"),
        value: MTTROptionValue.PRODUCT,
    },
    {
        label: getVocabulary("subProduct"),
        value: MTTROptionValue.SUBPRODUCT,
    },
    {
        label: "Risk Rating",
        value: MTTROptionValue.RISK_RATING,
    },
];

export const DueInGroupOptionValues = {
    severity: "severity",
    hierarchy: ARMORCODE_GROUP_KEY,
    riskRating: "riskRating"
}
export const getDueInGroupOptions = () => [{
    label: "Severity",
    value: DueInGroupOptionValues.severity
}, {
    label: getVocabulary("group"),
    value: DueInGroupOptionValues.hierarchy
}, {
    label: "Risk Rating",
    value: DueInGroupOptionValues.riskRating
}]

export const UnsupportedFilterWidgetMessage = "This widget does not support all of the current dashboard filters";
export const UnsupportedFilterMessage = "This filter is not supported by all available widgets";

export const FilterKeysSupportedByAllWidgets = ["groups", "product", "productTags", "productStatus", "environmentName", "subProduct", "subProductTags", "subProductStatuses"];

export const SAVED_SEARCH_OPTION_VALUE = "savedSearch";
export const SavedSearchOption = {
    label: "Finding View Filters", 
    value: SAVED_SEARCH_OPTION_VALUE
};